<template>
    <div class="instant-unstake">
        <div v-if="!walletConnected" class="compass-info-box small">
            Connect your wallet to instantly unstake your stake accounts or LSTs.
        </div>

        <div v-else>
            <!-- Loading state -->
            <div v-if="loadingAccounts" class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-primary"></div>
                <span class="small text-muted ms-2">Loading stake accounts...</span>
            </div>

            <!-- No accounts -->
            <div v-else-if="unstakeable.length === 0 && lstBalances.length === 0" class="text-muted small py-2">
                No active stake accounts or LSTs found.
            </div>

            <div v-else>
                <!-- Stake Accounts Section -->
                <div v-if="unstakeable.length > 0">
                    <label class="form-label fw-bold small mb-2">Active Stake Accounts</label>
                    <div
                        v-for="account in unstakeable"
                        :key="account.address"
                        :class="['border rounded p-3 mb-2', { 'border-primary bg-light': selected?.address === account.address }]"
                        @click="selectAccount(account)"
                        style="cursor: pointer;"
                    >
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <div class="small fw-bold">
                                    <span v-if="isCompass(account.validator)" class="text-success">Compass</span>
                                    <span v-else>{{ getValidatorLabel(account.validator) }}</span>
                                </div>
                                <div class="font-mono small text-muted">{{ short(account.address) }}</div>
                            </div>
                            <div class="text-end">
                                <div class="font-mono-num fw-bold">{{ fmtSol(account.solBalance) }} SOL</div>
                                <div class="small text-muted">{{ account.status }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- LST Balances Section -->
                <div v-if="lstBalances.length > 0" :class="{ 'mt-3': unstakeable.length > 0 }">
                    <label class="form-label fw-bold small mb-2">Liquid Staking Tokens</label>
                    <div
                        v-for="lst in lstBalances"
                        :key="lst.mint"
                        :class="['border rounded p-3 mb-2', { 'border-primary bg-light': selectedLst?.mint === lst.mint }]"
                        @click="selectLst(lst)"
                        style="cursor: pointer;"
                    >
                        <div class="d-flex justify-content-between align-items-center">
                            <div>
                                <span class="fw-bold small">{{ lst.symbol }}</span>
                            </div>
                            <div class="text-end">
                                <div class="font-mono-num fw-bold">{{ fmtSol(lst.amount) }} {{ lst.symbol }}</div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Selected Stake Account: Quote + Execute -->
                <div v-if="selected" class="compass-card mt-3 p-3">
                    <h6 class="mb-2">Instant Unstake: {{ fmtSol(selected.solBalance) }} SOL</h6>
                    <p class="small text-muted mb-2">
                        Your active stake account will be deposited into Marinade and converted to SOL.
                        <strong>Marinade Direct</strong> uses their liquid unstake pool.
                        <strong>Jupiter</strong> routes through Sanctum for potentially better rates.
                    </p>

                    <!-- Provider selection -->
                    <label class="form-label small fw-bold">Provider</label>
                    <div class="d-flex gap-2 mb-3">
                        <button
                            v-for="p in providers"
                            :key="p.id"
                            :class="['btn btn-sm', provider === p.id ? 'btn-primary' : 'btn-outline-secondary']"
                            @click="provider = p.id; fetchQuote()"
                        >
                            {{ p.label }}
                        </button>
                    </div>

                    <!-- Quote -->
                    <div v-if="fetchingQuote" class="text-center py-2">
                        <div class="spinner-border spinner-border-sm text-primary"></div>
                        <span class="small text-muted ms-2">Fetching quote...</span>
                    </div>

                    <div v-else-if="quote" class="compass-info-box mb-3" style="padding: 0.75rem;">
                        <div class="d-flex justify-content-between small mb-1">
                            <span>Stake Account</span>
                            <span class="font-mono-num">{{ fmtSol(selected.solBalance) }} SOL</span>
                        </div>
                        <div class="d-flex justify-content-between small text-muted">
                            <span>Provider Fee ({{ quote.feePct }}%)</span>
                            <span class="font-mono-num">-{{ fmtSol(quote.fee) }} SOL</span>
                        </div>
                        <div v-if="compassFee > 0" class="d-flex justify-content-between small text-muted">
                            <span>Compass Fee ({{ (UNSTAKE_FEE_BPS / 100).toFixed(1) }}%)</span>
                            <span class="font-mono-num">-{{ fmtSol(compassFee) }} SOL</span>
                        </div>
                        <div class="d-flex justify-content-between small mb-1 pt-1 border-top">
                            <span>You Receive</span>
                            <span class="font-mono-num fw-bold pct-change pct-positive">{{ fmtSol(quote.solOut - compassFee) }} SOL</span>
                        </div>
                        <div class="d-flex justify-content-between small text-muted">
                            <span>Via</span>
                            <span>{{ quote.providerName }}</span>
                        </div>
                    </div>

                    <div v-if="quoteError" class="alert alert-warning small py-2 mb-3">{{ quoteError }}</div>

                    <button
                        class="compass-btn compass-btn--primary w-100"
                        @click="executeStakeAccountUnstake"
                        :disabled="!quote || executing"
                    >
                        <span v-if="executing" class="spinner-border spinner-border-sm me-2"></span>
                        {{ executing ? 'Processing...' : 'Instant Unstake → SOL' }}
                    </button>
                </div>

                <!-- Selected LST: Quote + Execute -->
                <div v-if="selectedLst && !selected" class="compass-card mt-3 p-3">
                    <h6 class="mb-2">Swap {{ selectedLst.symbol }} → SOL</h6>

                    <label class="form-label small">Amount</label>
                    <div class="input-group mb-3">
                        <input
                            type="number"
                            class="form-control font-mono-num"
                            v-model.number="lstAmount"
                            :max="selectedLst.amount"
                            step="0.01"
                            placeholder="0.00"
                        >
                        <button class="btn btn-outline-secondary" @click="lstAmount = selectedLst.amount">MAX</button>
                    </div>

                    <div v-if="lstQuote" class="compass-info-box mb-3" style="padding: 0.75rem;">
                        <div class="d-flex justify-content-between small">
                            <span>You Receive</span>
                            <span class="font-mono-num fw-bold">{{ fmtSol(parseInt(lstQuote.outAmount) / 1e9) }} SOL</span>
                        </div>
                        <div v-if="lstQuote.priceImpactPct" class="d-flex justify-content-between small text-muted">
                            <span>Price Impact</span>
                            <span>{{ lstQuote.priceImpactPct }}%</span>
                        </div>
                    </div>

                    <button
                        class="compass-btn compass-btn--primary w-100"
                        @click="executeLstSwap"
                        :disabled="!lstAmount || lstAmount <= 0 || executing"
                    >
                        <span v-if="executing" class="spinner-border spinner-border-sm me-2"></span>
                        {{ executing ? 'Swapping...' : `Swap ${selectedLst.symbol} → SOL` }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Result / Error -->
        <div v-if="result" class="alert alert-success mt-3 small">
            {{ result.message }}
            <a v-if="result.explorerUrl" :href="result.explorerUrl" target="_blank" class="ms-1">
                View transaction &rarr;
            </a>
        </div>

        <!-- Post-unstake: Restake or get compassSOL -->
        <div v-if="result && !postAction" class="compass-card compass-card--accent mt-3 p-3">
            <h6 class="mb-2 text-gradient">Put your SOL back to work</h6>
            <p class="small text-muted mb-3">
                Earn {{ compassApy }}% APY with Compass — 0% commission, Jito MEV rewards included.
            </p>
            <div class="d-flex gap-2 flex-wrap">
                <button class="compass-btn compass-btn--primary compass-btn--sm flex-fill" @click="restakeWithCompass" :disabled="postActionLoading">
                    <span v-if="postActionLoading && postActionType === 'restake'" class="spinner-border spinner-border-sm me-1"></span>
                    Restake with Compass
                </button>
                <button v-if="compasssolMint" class="compass-btn compass-btn--outline compass-btn--sm flex-fill" @click="swapToCompassSol" :disabled="postActionLoading">
                    <span v-if="postActionLoading && postActionType === 'compasssol'" class="spinner-border spinner-border-sm me-1"></span>
                    Get compassSOL
                </button>
            </div>
        </div>

        <div v-if="postAction" class="alert alert-success mt-3 small">
            {{ postAction.message }}
            <a v-if="postAction.explorerUrl" :href="postAction.explorerUrl" target="_blank" class="ms-1">View tx &rarr;</a>
        </div>

        <div v-if="error" class="alert alert-danger mt-3 small">{{ error }}</div>

        <!-- Free alternative -->
        <div class="small text-muted mt-3 pt-2 border-top" v-if="walletConnected">
            <strong>Prefer free unstaking?</strong> Use the standard withdraw on your
            <a href="/app/stake">Stake Dashboard</a> — SOL available after epoch ends
            (~{{ epochTimeRemaining || '2.5 days' }}).
        </div>
    </div>
</template>

<script>
import { useSolSdk } from '../composables/useSolSdk';

const COMPASS_VOTE = 'EARNynHRWg6GfyJCmrrizcZxARB3HVzcaasvNa8kBS72';
const COMPASS_IDENTITY = 'CMPSSdrTnRQBiBGTyFpdCc3VMNuLWYWaSkE8Zh5z6gbd';
const MARINADE_REFERRAL = 'D9d7WxBeCeNU8zECA4YKG6ciBKHQG5mqmyygCetvXz7H';
const SOL_MINT = 'So11111111111111111111111111111111111111112';
const MSOL_MINT = 'mSoLzYCxHdYgdzU16g5QSh3i5K3z3KZK7ytfqcJm7So';

// Unstake fee: configurable basis points sent to Compass as SOL transfer
// 30 BPS = 0.3% — revenue directed to the Compass validator
const UNSTAKE_FEE_BPS = window.unstakeFeeBPS ?? 30;

const KNOWN_LSTS = {
    [MSOL_MINT]: { symbol: 'mSOL', decimals: 9 },
    'J1toso1uCk3RLmjorhTtrVwY9HJ7X8V9yYac6Y7kGCPn': { symbol: 'jitoSOL', decimals: 9 },
    'bSo13r4TkiE4KumL71LsHTPpL2euBYLFx6h9HP3piy1': { symbol: 'bSOL', decimals: 9 },
    'Comp4ssDzXcLeu2MnLuGNNFC4cmLPMng8qWHPvzAMU1h': { symbol: 'compassSOL', decimals: 9 },
    'vSoLxydx6akxyMD9XEcPvGYNGq6Nn66oqVb3UkGkei7': { symbol: 'vSOL', decimals: 9 },
    'he1iusmfkpAdwvxLNGV8Y1iSbj4rUy6yMhEA3fotn9A': { symbol: 'hSOL', decimals: 9 },
    'jupSoLaHXQiZZTSfEWMTRRgpnyFm8f6sZdosWBjx93v': { symbol: 'jupSOL', decimals: 9 },
    'LSoLi4A4Pk4i8DPFYcfHziRdEbH9otvSJcSrkMVq99c': { symbol: 'laineSOL', decimals: 9 },
};

export default {
    name: 'InstantUnstake',
    props: {
        epochTimeRemaining: { type: String, default: null },
        // When used inside StakeOptimizer, a single account can be passed directly
        stakeAccount: { type: Object, default: null },
        // Compass validator data for post-unstake restake/compassSOL flow
        compassVote: { type: String, default: 'EARNynHRWg6GfyJCmrrizcZxARB3HVzcaasvNa8kBS72' },
        compassApy: { type: Number, default: 7.2 },
        compasssolMint: { type: String, default: 'Comp4ssDzXcLeu2MnLuGNNFC4cmLPMng8qWHPvzAMU1h' },
    },
    data() {
        return {
            loadingAccounts: false,
            stakeAccounts: [],
            lstBalances: [],
            selected: null,       // selected stake account
            selectedLst: null,    // selected LST
            provider: 'marinade', // 'marinade' or 'jupiter'
            providers: [
                { id: 'marinade', label: 'Marinade Direct' },
                { id: 'jupiter', label: 'Jupiter (Sanctum)' },
            ],
            fetchingQuote: false,
            quote: null,
            quoteError: null,
            lstAmount: null,
            lstQuote: null,
            executing: false,
            result: null,
            error: null,
            postAction: null,
            postActionLoading: false,
            postActionType: null,
        };
    },
    computed: {
        walletConnected() {
            return !!window.wallet?.publicKey?.value;
        },
        unstakeable() {
            return this.stakeAccounts.filter(a =>
                a.status === 'active' || a.status === 'delegated'
            );
        },
        UNSTAKE_FEE_BPS() {
            return UNSTAKE_FEE_BPS;
        },
        compassFee() {
            if (!this.quote || UNSTAKE_FEE_BPS <= 0) return 0;
            return this.quote.solOut * (UNSTAKE_FEE_BPS / 10000);
        },
    },
    watch: {
        walletConnected(val) {
            if (val) this.loadAll();
        },
        lstAmount() {
            if (this._lstDebounce) clearTimeout(this._lstDebounce);
            this._lstDebounce = setTimeout(() => this.fetchLstQuote(), 500);
        },
        selectedLst() {
            this.lstAmount = null;
            this.lstQuote = null;
        },
    },
    mounted() {
        // If a stake account was passed as prop (from StakeOptimizer), use it directly
        if (this.stakeAccount) {
            this.stakeAccounts = [this.stakeAccount];
            this.selected = this.stakeAccount;
            if (this.walletConnected) this.fetchQuote();
            return;
        }
        if (this.walletConnected) this.loadAll();
        this._walletPoll = setInterval(() => {
            if (this.walletConnected && !this.stakeAccounts.length && !this.loadingAccounts) {
                this.loadAll();
            }
        }, 2000);
    },
    beforeUnmount() {
        if (this._walletPoll) clearInterval(this._walletPoll);
        if (this._lstDebounce) clearTimeout(this._lstDebounce);
    },
    methods: {
        fmtSol(v) {
            if (v == null) return '—';
            return Number(v).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
        },
        short(addr) {
            return addr ? addr.slice(0, 6) + '...' + addr.slice(-4) : '—';
        },
        isCompass(v) { return v === COMPASS_VOTE; },
        getValidatorLabel(v) {
            if (!v) return 'Unknown';
            if (this.isCompass(v)) return 'Compass';
            return this.short(v);
        },

        selectAccount(account) {
            this.selected = this.selected?.address === account.address ? null : account;
            this.selectedLst = null;
            this.quote = null;
            this.quoteError = null;
            this.result = null;
            this.error = null;
            if (this.selected) this.fetchQuote();
        },

        selectLst(lst) {
            this.selectedLst = this.selectedLst?.mint === lst.mint ? null : lst;
            this.selected = null;
            this.quote = null;
            this.result = null;
            this.error = null;
        },

        async loadAll() {
            await Promise.all([this.loadStakeAccounts(), this.loadLstBalances()]);
        },

        async loadStakeAccounts() {
            if (!this.walletConnected) return;
            this.loadingAccounts = true;
            try {
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();
                const wallet = window.wallet.publicKey.value.toBase58();
                this.stakeAccounts = await sdk.stake.getStakeAccounts(wallet);
            } catch (e) {
                console.error('[InstantUnstake] load accounts:', e);
            } finally {
                this.loadingAccounts = false;
            }
        },

        async loadLstBalances() {
            if (!this.walletConnected) return;
            try {
                const { Connection, PublicKey } = await import('@solana/web3.js');
                const { TOKEN_PROGRAM_ID } = await import('@solana/spl-token');
                const conn = new Connection(window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com');
                const accts = await conn.getParsedTokenAccountsByOwner(
                    window.wallet.publicKey.value,
                    { programId: TOKEN_PROGRAM_ID }
                );
                this.lstBalances = accts.value
                    .map(({ account }) => {
                        const info = account.data.parsed?.info;
                        if (!info) return null;
                        const meta = KNOWN_LSTS[info.mint];
                        if (!meta) return null;
                        const amount = parseFloat(info.tokenAmount?.uiAmountString || '0');
                        if (amount <= 0.0001) return null;
                        return { mint: info.mint, amount, ...meta };
                    })
                    .filter(Boolean);
            } catch (e) {
                console.error('[InstantUnstake] load LSTs:', e);
            }
        },

        // ─── Stake Account Instant Unstake ───

        async fetchQuote() {
            if (!this.selected) return;
            this.fetchingQuote = true;
            this.quote = null;
            this.quoteError = null;

            const lamports = Math.floor(this.selected.solBalance * 1e9);

            if (this.provider === 'marinade') {
                await this.fetchMarinadeQuote(lamports);
            } else {
                await this.fetchJupiterQuote(lamports);
            }
            this.fetchingQuote = false;
        },

        async fetchMarinadeQuote(lamports) {
            // Marinade flow: depositStakeAccount → mSOL → liquidUnstake → SOL
            // Fee depends on Marinade's liquid staking pool state
            try {
                const { Marinade, MarinadeConfig } = await import('@marinade.finance/marinade-ts-sdk');
                const { Connection, PublicKey } = await import('@solana/web3.js');
                const conn = new Connection(window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com');

                const config = new MarinadeConfig({
                    connection: conn,
                    publicKey: window.wallet.publicKey.value,
                    referralCode: new PublicKey(MARINADE_REFERRAL),
                });
                const marinade = new Marinade(config);
                const state = await marinade.getMarinadeState();

                // mSol price from state (SOL per mSOL, e.g. 1.08)
                const mSolPrice = state.mSolPrice;
                // depositStakeAccount converts stake SOL → mSOL at mSolPrice
                const mSolReceived = lamports / mSolPrice;
                // liquidUnstake converts mSOL → SOL at mSolPrice minus pool fee
                // Marinade liquid unstake fee: typically 0.1-0.3% depending on pool utilization
                const liqPoolFee = state.liqPool?.lpMinFee?.basisPoints
                    ? state.liqPool.lpMinFee.basisPoints / 10000
                    : 0.003;
                // SOL received = mSolReceived * mSolPrice * (1 - fee)
                const solBeforeFee = (mSolReceived * mSolPrice) / 1e9;
                const fee = solBeforeFee * liqPoolFee;
                const solOut = solBeforeFee - fee;

                this.quote = {
                    solOut,
                    fee,
                    feePct: (liqPoolFee * 100).toFixed(2),
                    providerName: 'Marinade (deposit + liquid unstake)',
                    provider: 'marinade',
                    mSolPrice,
                };
            } catch (e) {
                console.warn('[InstantUnstake] Marinade quote error:', e);
                // Fallback estimate
                const fee = (lamports / 1e9) * 0.003;
                this.quote = {
                    solOut: (lamports / 1e9) - fee,
                    fee,
                    feePct: '0.3',
                    providerName: 'Marinade (estimated)',
                    provider: 'marinade',
                };
                this.quoteError = 'Live quote unavailable, showing estimate (~0.3% fee).';
            }
        },

        async fetchJupiterQuote(lamports) {
            // Jupiter/Sanctum path: depositStakeAccount → mSOL → Jupiter swap → SOL
            // Jupiter routes through Sanctum Router and can often find better rates
            try {
                // First, estimate mSOL we'd receive from Marinade deposit
                let mSolLamports;
                try {
                    const { Marinade, MarinadeConfig } = await import('@marinade.finance/marinade-ts-sdk');
                    const { Connection, PublicKey } = await import('@solana/web3.js');
                    const conn = new Connection(window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com');
                    const config = new MarinadeConfig({
                        connection: conn,
                        publicKey: window.wallet.publicKey.value,
                    });
                    const marinade = new Marinade(config);
                    const state = await marinade.getMarinadeState();
                    const mSolPrice = state.mSolPrice; // SOL per mSOL
                    mSolLamports = Math.floor(lamports / mSolPrice);
                } catch (e) {
                    // Fallback: estimate mSOL at ~1.08 SOL per mSOL
                    mSolLamports = Math.floor(lamports / 1.08);
                }

                // Get real Jupiter quote for mSOL → SOL
                const resp = await fetch(
                    `https://quote-api.jup.ag/v6/quote?inputMint=${MSOL_MINT}&outputMint=${SOL_MINT}&amount=${mSolLamports}&slippageBps=50`
                );
                if (!resp.ok) throw new Error('Jupiter quote unavailable');
                const jupQuote = await resp.json();
                if (jupQuote.error) throw new Error(jupQuote.error);

                const solOut = parseInt(jupQuote.outAmount) / 1e9;
                const solIn = lamports / 1e9;
                const fee = solIn - solOut;
                const feePct = ((fee / solIn) * 100).toFixed(2);

                this.quote = {
                    solOut,
                    fee: Math.max(0, fee),
                    feePct,
                    providerName: 'Jupiter via Sanctum Router',
                    provider: 'jupiter',
                    jupQuote, // Store full Jupiter quote for execution
                    mSolLamports,
                };
            } catch (e) {
                console.warn('[InstantUnstake] Jupiter quote error:', e);
                this.quoteError = 'Jupiter quote unavailable. Try Marinade Direct.';
            }
        },

        async executeStakeAccountUnstake() {
            if (!this.selected || !this.quote) return;
            this.executing = true;
            this.error = null;
            this.result = null;

            try {
                if (this.quote.provider === 'marinade') {
                    await this.executeMarinadeUnstake();
                } else {
                    await this.executeJupiterUnstake();
                }
                setTimeout(() => this.loadStakeAccounts(), 3000);
            } catch (e) {
                this.error = e.message || 'Instant unstake failed';
                console.error('[InstantUnstake] execute failed:', e);
            } finally {
                this.executing = false;
            }
        },

        /**
         * Send Compass unstake fee as a SOL transfer.
         * Non-blocking: if fee transfer fails, the unstake still succeeds.
         */
        async sendCompassFee(conn, solAmount) {
            if (UNSTAKE_FEE_BPS <= 0 || solAmount <= 0) return null;
            const feeSol = solAmount * (UNSTAKE_FEE_BPS / 10000);
            if (feeSol < 0.000001) return null; // Skip dust

            try {
                const { PublicKey, Transaction, SystemProgram } = await import('@solana/web3.js');
                const feeLamports = Math.floor(feeSol * 1e9);

                const tx = new Transaction().add(
                    SystemProgram.transfer({
                        fromPubkey: window.wallet.publicKey.value,
                        toPubkey: new PublicKey(COMPASS_IDENTITY),
                        lamports: feeLamports,
                    })
                );

                const hash = await conn.getLatestBlockhash();
                tx.recentBlockhash = hash.blockhash;
                tx.feePayer = window.wallet.publicKey.value;

                const sig = await window.wallet.sendTransaction(tx, conn);
                return sig;
            } catch (e) {
                // Fee transfer is best-effort — don't block the user
                console.warn('[InstantUnstake] Compass fee transfer failed:', e);
                return null;
            }
        },

        async executeMarinadeUnstake() {
            const { Marinade, MarinadeConfig, BN } = await import('@marinade.finance/marinade-ts-sdk');
            const { Connection, PublicKey } = await import('@solana/web3.js');
            const conn = new Connection(window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com');

            const config = new MarinadeConfig({
                connection: conn,
                publicKey: window.wallet.publicKey.value,
                referralCode: new PublicKey(MARINADE_REFERRAL),
            });
            const marinade = new Marinade(config);

            // Step 1: Deposit stake account into Marinade → receive mSOL
            const stakeAddr = new PublicKey(this.selected.address);
            const depositResult = await marinade.depositStakeAccount(stakeAddr);

            const depositSig = await window.wallet.sendTransaction(
                depositResult.transaction,
                conn
            );
            await conn.confirmTransaction(depositSig, 'confirmed');

            // Step 2: Liquid unstake mSOL → SOL
            const mSolMint = new PublicKey(MSOL_MINT);
            const walletKey = window.wallet.publicKey.value;
            const mSolAccounts = await conn.getParsedTokenAccountsByOwner(walletKey, { mint: mSolMint });
            let mSolBalance = new BN(0);
            for (const { account } of mSolAccounts.value) {
                const amt = account.data.parsed?.info?.tokenAmount?.amount;
                if (amt) mSolBalance = mSolBalance.add(new BN(amt));
            }

            const unstakeResult = await marinade.liquidUnstake(mSolBalance);
            const unstakeSig = await window.wallet.sendTransaction(
                unstakeResult.transaction,
                conn
            );

            // Step 3: Send Compass fee (best-effort)
            await this.sendCompassFee(conn, this.quote.solOut);

            this.result = {
                message: `Instantly unstaked ~${this.fmtSol(this.quote.solOut - this.compassFee)} SOL via Marinade.`,
                explorerUrl: `https://solscan.io/tx/${unstakeSig}`,
            };
        },

        async executeJupiterUnstake() {
            // Jupiter/Sanctum path: depositStakeAccount → mSOL → Jupiter swap → SOL
            // Step 1: Deposit stake account into Marinade → mSOL (same as Marinade path)
            const { Marinade, MarinadeConfig, BN } = await import('@marinade.finance/marinade-ts-sdk');
            const { Connection, PublicKey, VersionedTransaction } = await import('@solana/web3.js');
            const conn = new Connection(window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com');

            const config = new MarinadeConfig({
                connection: conn,
                publicKey: window.wallet.publicKey.value,
                referralCode: new PublicKey(MARINADE_REFERRAL),
            });
            const marinade = new Marinade(config);

            const stakeAddr = new PublicKey(this.selected.address);
            const depositResult = await marinade.depositStakeAccount(stakeAddr);
            const depositSig = await window.wallet.sendTransaction(depositResult.transaction, conn);
            console.log('[InstantUnstake] Deposit stake → mSOL tx:', depositSig);
            await conn.confirmTransaction(depositSig, 'confirmed');

            // Step 2: Get actual mSOL balance after deposit
            const mSolMint = new PublicKey(MSOL_MINT);
            const walletKey = window.wallet.publicKey.value;
            const mSolAccounts = await conn.getParsedTokenAccountsByOwner(walletKey, { mint: mSolMint });
            let mSolBalance = 0;
            for (const { account } of mSolAccounts.value) {
                const amt = account.data.parsed?.info?.tokenAmount?.amount;
                if (amt) mSolBalance += parseInt(amt);
            }

            if (mSolBalance <= 0) throw new Error('No mSOL received from deposit');

            // Step 3: Swap mSOL → SOL via Jupiter (routes through Sanctum Router for best rate)
            const quoteResp = await fetch(
                `https://quote-api.jup.ag/v6/quote?inputMint=${MSOL_MINT}&outputMint=${SOL_MINT}&amount=${mSolBalance}&slippageBps=50`
            );
            const jupQuote = await quoteResp.json();
            if (jupQuote.error) throw new Error(jupQuote.error);

            const swapResp = await fetch('https://quote-api.jup.ag/v6/swap', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    quoteResponse: jupQuote,
                    userPublicKey: walletKey.toBase58(),
                    wrapAndUnwrapSol: true,
                }),
            });
            const { swapTransaction } = await swapResp.json();
            if (!swapTransaction) throw new Error('Failed to build Jupiter swap transaction');

            const tx = VersionedTransaction.deserialize(Buffer.from(swapTransaction, 'base64'));
            const signed = await window.wallet.signTransaction(tx);
            const swapSig = await conn.sendRawTransaction(signed.serialize());

            const solOut = parseInt(jupQuote.outAmount) / 1e9;

            // Send Compass fee (best-effort)
            await this.sendCompassFee(conn, solOut);

            this.result = {
                message: `Instantly unstaked ~${this.fmtSol(solOut - (solOut * UNSTAKE_FEE_BPS / 10000))} SOL via Jupiter/Sanctum Router.`,
                explorerUrl: `https://solscan.io/tx/${swapSig}`,
            };
        },

        // ─── LST Swap ───

        async fetchLstQuote() {
            if (!this.selectedLst || !this.lstAmount || this.lstAmount <= 0) {
                this.lstQuote = null;
                return;
            }
            try {
                const amountLamports = Math.floor(this.lstAmount * 1e9);
                const resp = await fetch(
                    `https://quote-api.jup.ag/v6/quote?inputMint=${this.selectedLst.mint}&outputMint=${SOL_MINT}&amount=${amountLamports}&slippageBps=50`
                );
                if (resp.ok) this.lstQuote = await resp.json();
            } catch (e) {
                console.error('[InstantUnstake] LST quote:', e);
            }
        },

        async executeLstSwap() {
            if (!this.selectedLst || !this.lstAmount) return;
            this.executing = true;
            this.error = null;
            this.result = null;

            try {
                const amountLamports = Math.floor(this.lstAmount * 1e9);
                const wallet = window.wallet.publicKey.value.toBase58();

                const quoteResp = await fetch(
                    `https://quote-api.jup.ag/v6/quote?inputMint=${this.selectedLst.mint}&outputMint=${SOL_MINT}&amount=${amountLamports}&slippageBps=50`
                );
                const quote = await quoteResp.json();
                if (quote.error) throw new Error(quote.error);

                const swapResp = await fetch('https://quote-api.jup.ag/v6/swap', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        quoteResponse: quote,
                        userPublicKey: wallet,
                        wrapAndUnwrapSol: true,
                    }),
                });
                const { swapTransaction } = await swapResp.json();
                if (!swapTransaction) throw new Error('Failed to build swap transaction');

                const { VersionedTransaction, Connection } = await import('@solana/web3.js');
                const tx = VersionedTransaction.deserialize(Buffer.from(swapTransaction, 'base64'));
                const signed = await window.wallet.signTransaction(tx);

                const conn = new Connection(window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com');
                const sig = await conn.sendRawTransaction(signed.serialize());

                this.result = {
                    message: `Swapped ${this.lstAmount.toFixed(4)} ${this.selectedLst.symbol} → ~${this.fmtSol(parseInt(quote.outAmount) / 1e9)} SOL`,
                    explorerUrl: `https://solscan.io/tx/${sig}`,
                };
                setTimeout(() => this.loadLstBalances(), 3000);
            } catch (e) {
                this.error = e.message || 'Swap failed';
            } finally {
                this.executing = false;
            }
        },

        // ─── Post-Unstake: Restake with Compass ───

        async restakeWithCompass() {
            this.postActionLoading = true;
            this.postActionType = 'restake';
            this.error = null;
            try {
                const { getSdk } = useSolSdk();
                const sdk = await getSdk();

                // Get current SOL balance, leave 0.01 for fees
                const { Connection, PublicKey } = await import('@solana/web3.js');
                const conn = new Connection(window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com');
                const balance = await conn.getBalance(window.wallet.publicKey.value);
                const availableSol = Math.max(0, (balance / 1e9) - 0.01);

                if (availableSol < 0.01) {
                    this.error = 'Insufficient SOL balance to stake (need at least 0.02 SOL).';
                    return;
                }

                const stakeResult = await sdk.stake.createAndDelegateStake(
                    'browser',
                    availableSol,
                    this.compassVote
                );

                this.postAction = {
                    message: `Staked ${availableSol.toFixed(4)} SOL with Compass! Earning ${this.compassApy}% APY.`,
                    explorerUrl: stakeResult.explorerUrl,
                };
            } catch (e) {
                this.error = e.message || 'Failed to restake with Compass';
            } finally {
                this.postActionLoading = false;
                this.postActionType = null;
            }
        },

        async swapToCompassSol() {
            if (!this.compasssolMint) return;
            this.postActionLoading = true;
            this.postActionType = 'compasssol';
            this.error = null;
            try {
                // Get current SOL balance, leave 0.01 for fees
                const { Connection, PublicKey, VersionedTransaction } = await import('@solana/web3.js');
                const conn = new Connection(window.swapConfig?.rpcEndpoint || 'https://api.mainnet-beta.solana.com');
                const balance = await conn.getBalance(window.wallet.publicKey.value);
                const availableSol = Math.max(0, (balance / 1e9) - 0.01);

                if (availableSol < 0.01) {
                    this.error = 'Insufficient SOL balance to swap.';
                    return;
                }

                const lamports = Math.floor(availableSol * 1e9);
                const wallet = window.wallet.publicKey.value.toBase58();

                // Get Jupiter quote SOL → compassSOL
                const quoteResp = await fetch(
                    `https://quote-api.jup.ag/v6/quote?inputMint=${SOL_MINT}&outputMint=${this.compasssolMint}&amount=${lamports}&slippageBps=50`
                );
                const quote = await quoteResp.json();
                if (quote.error) throw new Error(quote.error);

                const swapResp = await fetch('https://quote-api.jup.ag/v6/swap', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        quoteResponse: quote,
                        userPublicKey: wallet,
                        wrapAndUnwrapSol: true,
                    }),
                });
                const { swapTransaction } = await swapResp.json();
                if (!swapTransaction) throw new Error('Failed to build swap transaction');

                const tx = VersionedTransaction.deserialize(Buffer.from(swapTransaction, 'base64'));
                const signed = await window.wallet.signTransaction(tx);
                const sig = await conn.sendRawTransaction(signed.serialize());

                const compassSolOut = parseInt(quote.outAmount) / 1e9;
                this.postAction = {
                    message: `Swapped to ~${compassSolOut.toFixed(4)} compassSOL! Earning liquid staking yield.`,
                    explorerUrl: `https://solscan.io/tx/${sig}`,
                };
            } catch (e) {
                this.error = e.message || 'Failed to swap to compassSOL';
            } finally {
                this.postActionLoading = false;
                this.postActionType = null;
            }
        },
    },
};
</script>
